import type UpgradeModal from '@/components/modal/UpgradeModal'

import { proxy, useSnapshot } from 'valtio'
import { deepClone, devtools } from 'valtio/utils'

import { resetStore } from '@/lib/valtio/reset-store'

import type { Rundown } from '~shared/types'

import type { ModalControls } from '../modal-manager/model/types'

//
// TODO: turn this into UI store, and create rundown domain model store
//

type RundownPageState = {
  rundown: Rundown

  /** Legacy modals - migrating to modalStore */
  modals: {
    upgradeModal: ModalControls<typeof UpgradeModal> | null // TODO: Migrate legacy modals
  }
}

const initialState = {
  rundown: {} as Rundown,

  modals: {
    upgradeModal: null,
  },
} as const satisfies RundownPageState

export const rundownPageStore = proxy<RundownPageState>(deepClone(initialState))

// #region ============== DEV
if (process.env.NODE_ENV !== 'production') {
  devtools(rundownPageStore, { name: 'rundownPageStore', enabled: true })
}
// #endregion

export function initRundownPageStore(rundown: Rundown) {
  // Make a copy to avoid mutated references
  const clonedRundown = structuredClone(rundown)

  rundownPageStore.rundown = clonedRundown
}

export function resetRundownPageStore() {
  return resetStore(rundownPageStore, initialState)
}

// #region ============== HOOKS

export function useRundownPageSnap() {
  return useSnapshot(rundownPageStore)
}

/**
 * Convenience hook for accessing snapshot of `rundown` in rundownPageStore.
 *
 * Use only for reading `rundown.id` (will be replaced soon).
 * Use `useRundownPageSnap()` for everything else.
 *
 * @remarks
 *   Subscribes to the `rundown` object itself, and `initRundownPageStore`
 *   replaces that object wholesale with a clone. Valtio does not notify old
 *   subscribers when a parent property is reassigned. Safe for values that are
 *   stable for the life of the page (ie. `id`).
 */
export function useRundownSnap() {
  if (!rundownPageStore.rundown) throw new Error('Rundown not initalized')
  return useSnapshot(rundownPageStore.rundown)
}

// #endregion
